import { AttachFile, ErrorOutline, UploadFileOutlined } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Grid } from '@mui/system'
import { useMutation } from '@tanstack/react-query'
import { MuiFileInput } from 'mui-file-input'
import React, { FunctionComponent, useState } from 'react'

import { useError } from '../../../../base/contexts/ErrorProvider.tsx'
import { swalClose, swalLoading } from '../../../../utils/swal'
import { apiArticuloImportar } from '../../api/apiArticuloImportar'

interface OwnProps {
  id: string
  keepMounted: boolean
  open: boolean
  onClose: (value?: boolean) => void
}

type Props = OwnProps

/**
 * Modulo de importación de articulos masivo
 * @param props
 * @constructor
 */
const ArticuloImportarDialog: FunctionComponent<Props> = (props) => {
  const { onClose, open, ...other } = props
  const [file, setFile] = useState<File | null>(null)
  const { showError } = useError()

  const { mutate, data, isPending, isError, error, reset } = useMutation({
    mutationFn: (fileToUpload: File) => apiArticuloImportar(fileToUpload),
    // 1. Justo antes de que empiece la mutación
    onMutate: () => {
      swalLoading('Subiendo archivo...')
    },
    // 2. Cuando la mutación termina (ya sea éxito o error)
    onSettled: () => {
      swalClose()
    },
    onSuccess: (res) => {
      if (res.success) {
        setFile(null)
        setTimeout(() => {
          onClose(true)
        }, 2000)
      }
    },
    onError: (err) => {
      showError(err)
      // swalError('Error', err.message);
    },
  })

  /** Evento onchange cuando selecciona el archivo */
  const handleFileChange = (selectedFile: File | null) => {
    if (selectedFile) {
      setFile(selectedFile)
      reset()
    }
  }

  /**
   * Envia el archivo al servidor
   */
  const handleUpload = () => {
    if (file) mutate(file)
  }

  /** Funcion de limpieza total */
  const handleExited = () => {
    setFile(null)
    reset()
  }

  return (
    <Dialog
      maxWidth="md"
      open={open}
      disableEnforceFocus
      disableAutoFocus
      slotProps={{
        transition: { onExited: handleExited },
      }}
      {...other}
    >
      <DialogTitle>Importar articulos</DialogTitle>
      <DialogContent dividers>
        <Alert color={'info'}>
          <Typography>
            Puede descargar la plantilla de importación de articulos desde el siguiente enlace.
            <Link
              href="https://cloud.isipaid.net/inv/inv_template_articulos_V4.xlsx"
              variant="body2"
              sx={{ ml: 1 }}
            >
              Descargar Plantilla
            </Link>
          </Typography>
        </Alert>
        <Grid container>
          <Grid size={12}>
            <MuiFileInput
              size={'medium'}
              fullWidth
              sx={{ mt: 3, mb: 0.5 }}
              label={'Archivo separado por ;'}
              value={file}
              slotProps={{
                htmlInput: { accept: '.csv' },
                input: {
                  startAdornment: <AttachFile />,
                },
              }}
              placeholder="Clic aqui para seleccionar archivo csv"
              clearIconButtonProps={{
                title: 'Quitar',
                children: <CloseIcon fontSize="small" />,
                onClick: () => {
                  setFile(null)
                },
              }}
              onChange={handleFileChange}
            />
            <small>Archivo seleccionado: {file ? file.name : 'NINGUNO'}</small>
          </Grid>
          <Grid size={12}>
            {isError && (
              <Alert severity="error" icon={<ErrorOutline />}>
                Ocurrió un error crítico: {(error as Error).message}
              </Alert>
            )}

            {data?.success && (
              <Alert severity="success">¡Importación completada con éxito! {data.estado}</Alert>
            )}

            {data && !data.success && data.detalle.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Se procesó el archivo, pero se encontraron errores en {data.detalle.length} filas.
                </Alert>

                <TableContainer component={Paper} variant={'outlined'} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Fila</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Articulo</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Errores Detectados</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.detalle.map((err, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{err.fila}</TableCell>
                          <TableCell>
                            <code>{err.columna}</code>
                          </TableCell>
                          <TableCell color="error.main">
                            {Array.isArray(err.errores) ? err.errores.join(', ') : err.errores}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'center' }}>
        <Button
          color={'error'}
          disabled={isPending}
          onClick={() => {
            onClose()
          }}
        >
          Cerrar
        </Button>
        <Button
          color={'orange'}
          loading={isPending}
          variant={'contained'}
          startIcon={<UploadFileOutlined />}
          onClick={handleUpload}
          disabled={!file}
        >
          Iniciar Importación
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ArticuloImportarDialog
